import type { FC } from 'react';
import { Box, type BoxProps, Card, type CardProps, CircularProgress, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMuiTheme } from '@/hooks/use-mui-theme';

const StyledBox = styled(Box)<BoxProps>(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const MyCard: FC<
  CardProps & {
    loading?: boolean;
  }
> = (props) => {
  const { loading, children, ...rest } = props;
  const { theme, isDark } = useMuiTheme();
  const opacity = isDark ? 0.2 : 0.8;
  const isDownSm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const position = isDownSm ? 'fixed' : 'absolute';

  return (
    <Card {...rest}>
      <Box className="relative z-0">{children}</Box>

      {loading && (
        <>
          <StyledBox
            sx={{
              position,
              backgroundColor: theme.palette.background.default,
              opacity,
              zIndex: 1,
              '&.MuiBox-root': {
                '@media (forced-colors: active)': {
                  backgroundColor: 'GrayText !important',
                },
              },
            }}
          ></StyledBox>
          <StyledBox
            sx={{
              zIndex: 2,
              position,
            }}
          >
            <CircularProgress size={24} aria-hidden="true" role="img" />
          </StyledBox>
        </>
      )}
    </Card>
  );
};

export const CenteredCard = styled(MyCard)<CardProps>(({ theme }) => ({
  backgroundColor: theme.vars?.palette.background.paper,
  boxShadow: 'var(--Paper-shadow)',
  position: 'relative',
  backgroundImage: 'var(--Paper-overlay)',
  marginTop: theme.spacing(2),
  '&.MuiCard-root': {
    overflow: 'unset',
  },
  [theme.breakpoints.down('sm')]: {
    boxShadow: 'none',
    backgroundColor: theme.vars?.palette.background.paper,
    backgroundImage: 'none',
    marginTop: theme.spacing(0),
  },
}));
